import React from 'react';
import { motion } from 'framer-motion';
import { BarChart3Icon, ClockIcon, PhoneIncomingIcon, PhoneMissedIcon, PhoneOutgoingIcon } from 'lucide-react';
import { TrendDelta } from '../common/TrendDelta';
import { AnimatedNumber } from '../common/AnimatedNumber';
import { RevealItem, revealContainer } from '../common/Reveal';
import { formatDuration, formatNumber, formatPercent } from '../../utils/format';
import type { AgentStats, KpiTrend } from '../../types/voip';
type Tone = 'accent' | 'positive' | 'danger';
const toneClass: Record<Tone, string> = {
  accent: 'bg-accent-soft text-accent',
  positive: 'bg-positive/10 text-positive',
  danger: 'bg-danger/10 text-danger'
};
const wholeNumber = (n: number) => formatNumber(Math.round(n));
interface KpiCardsProps {
  totals: AgentStats;
  trend: KpiTrend;
  previousLabel: string;
}
export function KpiCards({
  totals,
  trend,
  previousLabel
}: KpiCardsProps) {
  const cards = [{
    label: 'Total talk time',
    num: totals.talkSeconds,
    fmt: formatDuration,
    delta: trend.talkSeconds,
    Icon: ClockIcon,
    tone: 'accent' as Tone
  }, {
    label: 'Outbound calls',
    num: totals.outbound,
    fmt: wholeNumber,
    delta: trend.outbound,
    Icon: PhoneOutgoingIcon,
    tone: 'accent' as Tone
  }, {
    label: 'Answered calls',
    num: totals.inbound,
    fmt: wholeNumber,
    delta: trend.inbound,
    Icon: PhoneIncomingIcon,
    tone: 'positive' as Tone
  }, {
    label: 'Missed calls',
    num: totals.missed,
    fmt: wholeNumber,
    delta: trend.missed,
    invert: true,
    Icon: PhoneMissedIcon,
    tone: 'danger' as Tone
  }, {
    label: 'Answer rate',
    num: totals.answerRate,
    fmt: formatPercent,
    delta: trend.answerRate,
    Icon: BarChart3Icon,
    tone: 'positive' as Tone
  }];
  return <motion.section variants={revealContainer} aria-label="Key metrics" className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5" data-id="element-481" data-mp="r0stfqb3j1xg">
      {cards.map(({
      label,
      num,
      fmt,
      delta,
      invert,
      Icon,
      tone
    }) => <RevealItem key={label} fill data-id="element-482" data-mp="h3edrswzyh67">
          <div className="rounded-2xl border border-line bg-surface p-5 shadow-card" data-id="element-483" data-mp="h4fplzlu77oz">
            <span className={`flex h-9 w-9 items-center justify-center rounded-xl ${toneClass[tone]}`} data-id="element-484" data-mp="7d66oxgz94ip">
              <Icon className="h-[18px] w-[18px]" aria-hidden data-id="element-485" data-mp="cbhez3rmj6yt" />
            </span>
            <p className="mt-4 font-display text-[28px] font-bold leading-none tracking-tight text-ink" data-id="element-486" data-mp="3ukrpamm3dxjj">
              <AnimatedNumber value={num} format={fmt} data-id="element-487" data-mp="2fo2tlhthfhk" />
            </p>
            <p className="mt-1.5 text-sm text-muted" data-id="element-488" data-mp="q609yu58n873">{label}</p>
            <div className="mt-3 flex items-center gap-1.5" data-id="element-489" data-mp="v3scsj7f8eq2">
              <TrendDelta value={delta} invert={invert} data-id="element-490" data-mp="v3a5mrj80g3d" />
              <span className="truncate text-xs text-subtle" data-id="element-491" data-mp="hdggs97ptvtn">vs {previousLabel}</span>
            </div>
          </div>
        </RevealItem>)}
    </motion.section>;
}